import { Button, Icon, Spinner } from '@iwoca/orion';
import styles from './UploadedFiles.module.css';
import { TFile } from '../NewDocUploads.types';
import classNames from 'classnames';

type TFileStatus = 'loading' | 'error' | 'success' | 'rejected';

const UploadedFiles = ({
    files,
    removeFile
}: {
    files: TFile[];
    removeFile: (id: string) => void;
}) => {
    const renderIcon = (status: TFileStatus) => {
        if (status === 'loading') {
            return (
                <Spinner dataTestId="loading-icon" className={styles.spinner} />
            );
        }

        if (status === 'error') {
            return (
                <Icon
                    data-testid="error-icon"
                    width={20}
                    height={20}
                    icon={'exclamationTriangleOutline'}
                    className={classNames(styles.icon, styles.errorIcon)}
                />
            );
        }

        if (status === 'rejected') {
            return (
                <Icon
                    data-testid="rejected-icon"
                    width={20}
                    height={20}
                    icon={'exclamationTriangleOutline'}
                    className={classNames(styles.icon, styles.rejectedIcon)}
                />
            );
        }

        return (
            <Icon
                data-testid="success-icon"
                width={20}
                height={20}
                icon={'fileWithItemsOutline'}
                className={styles.icon}
            />
        );
    };

    return (
        <div className={styles.uploadedFilesContainer}>
            {files.map((file) => {
                const hasError = file.status === 'error';
                const isRejected = file.status === 'rejected';
                return (
                    <div
                        key={file.id}
                        data-testid={`uploaded-file-${file.id}`}
                        className={styles.uploadedFile}
                    >
                        <div className={styles.fileNameContainer}>
                            <div className={styles.iconContainer}>
                                {renderIcon(file.status)}
                            </div>
                            <p
                                className={classNames(styles.fileName, {
                                    [styles.errorFileName]: hasError
                                })}
                            >
                                {file.fileName}
                            </p>

                            {hasError && (
                                <Button
                                    data-testid="remove-button"
                                    aria-label="remove-button"
                                    size="small"
                                    variant="tertiary"
                                    onClick={() => removeFile(file.id)}
                                >
                                    <Icon
                                        height={20}
                                        width={20}
                                        icon="plus"
                                        className={styles.removeIcon}
                                    />
                                </Button>
                            )}
                        </div>

                        {file.errorReason && !isRejected && (
                            <div className={styles.errorMessageContainer}>
                                <p className={styles.errorMessage}>
                                    {file.errorReason}
                                </p>
                            </div>
                        )}

                        {!file.errorReason && isRejected && (
                            <div className={styles.errorMessageContainer}>
                                <p
                                    className={classNames(
                                        styles.errorMessage,
                                        styles.rejectedMessage
                                    )}
                                >
                                    {file.rejectReason}
                                </p>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default UploadedFiles;
